import { Info, LockKeyhole, LogOut, ShieldAlert, TriangleAlert, Users } from "lucide-react";
import { Navigate } from "react-router";

import { AtlasMark } from "@/components/layout/AtlasMark";
import { Button } from "@/components/ui/Button";
import { takeReturnTo, useAuth } from "@/auth/authState";

import microsoftMark from "@/assets/brand/microsoft-mark.svg";

import "./SignIn.css";

/**
 * Het inlogscherm. Het enige scherm buiten de applicatieschil.
 *
 * Vier gevallen, elk met een eigen volgende stap. Ze op één "er ging iets mis"
 * gooien zou de gebruiker precies datgene onthouden wat hij nodig heeft:
 *
 *   `signed-out`     inloggen met een Microsoft-account (Entra ID).
 *   `denied`         ingelogd, maar de backend accepteert het token niet.
 *                    In deze opzet vrijwel altijd: het account heeft nog geen
 *                    platformtenant (ADR-003, stap 4 van het runbook).
 *   `misconfigured`  de build mist de instellingen van de identity provider.
 *                    Dat is werk voor de beheerder, niet voor de gebruiker.
 *   `initialising`   MSAL wisselt de autorisatiecode nog in.
 *
 * Wat er níet staat: een manier om het inloggen over te slaan. Er is geen
 * demomodus en geen "kijk even rond"-knop. De backend geeft zonder geldig token
 * toch 401, en een knop die dat verbergt maakt van fail-closed een verrassing.
 */
export function SignIn() {
  const { status, mode, label, account, problem, signIn, signOut } = useAuth();

  // Wie hier komt terwijl hij al is ingelogd, gaat door naar het scherm waar hij
  // heen wilde — in één navigatie. Een effect ernaast zou met deze `<Navigate>`
  // om de eerste beurt vechten, en dat is een race die de gebruiker als "ik kom
  // altijd op het dashboard uit" merkt.
  if (status === "signed-in") {
    return <Navigate to={takeReturnTo() ?? "/"} replace />;
  }

  return (
    <main className="atl-signin">
      {/* Stijl A (28-10-2026): een effen navy vlak met het merk bovenaan en de
          slogan onderaan. De luchtfoto uit het ontwerp zit niet in de repo en
          wordt niet van buiten gehaald; het vlak werkt ook zonder. */}
      <section className="atl-signin__brand">
        <div className="atl-signin__lockup">
          <AtlasMark size={42} className="atl-signin__mark" />
          <div>
            <p className="atl-signin__eyebrow">Q-Estate B.V.</p>
            <p className="atl-signin__wordmark">
              Portefeuille<span>Atlas</span>
            </p>
          </div>
        </div>
        <div className="atl-signin__claim">
          <h1 className="atl-signin__slogan">
            Weten wat je hebt.
            <br />
            Kiezen wat je wilt.
          </h1>
          <p className="atl-signin__tagline">Eén sturingsplatform voor de vastgoedportefeuille.</p>
          <p className="atl-signin__confidential">
            <Info size={16} strokeWidth={1.75} aria-hidden />
            Vertrouwelijk. Toegang uitsluitend op uitnodiging.
          </p>
        </div>
      </section>

      <section className="atl-signin__panel">
        <div className="atl-signin__card">
          <Panel
            status={status}
            mode={mode}
            label={label}
            accountName={account?.displayName ?? null}
            problem={problem}
            onSignIn={signIn}
            onSignOut={signOut}
          />
          <p className="atl-signin__foot">Een platform van Q-Estate B.V. · gebouwd door BizzPower</p>
        </div>
      </section>
    </main>
  );
}

type PanelProps = {
  status: ReturnType<typeof useAuth>["status"];
  mode: ReturnType<typeof useAuth>["mode"];
  label: string;
  accountName: string | null;
  problem: string | null;
  onSignIn: () => Promise<void>;
  onSignOut: () => Promise<void>;
};

function Panel({ status, mode, label, accountName, problem, onSignIn, onSignOut }: PanelProps) {
  if (status === "initialising") {
    return (
      <div className="atl-signin__state" role="status">
        <h2 className="atl-signin__title">Bezig met aanmelden…</h2>
        <p className="atl-signin__body">De aanmelding bij {label} wordt afgerond.</p>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="atl-signin__state atl-signin__state--attention" role="status">
        <Icon tone="attention">
          <ShieldAlert size={20} strokeWidth={1.75} aria-hidden />
        </Icon>
        <h2 className="atl-signin__title">Nog geen toegang tot een portefeuille</h2>
        <p className="atl-signin__body">
          {accountName ? `${accountName} is aangemeld` : "De aanmelding is gelukt"}, maar het platform herkent nog
          geen portefeuille bij dit account. Dat is geen storing: zonder toegewezen corporatie weet het platform
          niet welke gegevens u mag zien, en dan toont het er liever geen.
        </p>
        <p className="atl-signin__body">Vraag de beheerder om uw account te koppelen aan een corporatie.</p>
        {problem && <p className="atl-signin__reference">Melding van de server: {problem}</p>}
        <Button variant="secondary" icon={<LogOut size={15} strokeWidth={2} aria-hidden />} onClick={onSignOut}>
          Uitloggen
        </Button>
      </div>
    );
  }

  if (status === "misconfigured") {
    return (
      <div className="atl-signin__state atl-signin__state--danger" role="alert">
        <Icon tone="danger">
          <TriangleAlert size={20} strokeWidth={1.75} aria-hidden />
        </Icon>
        <h2 className="atl-signin__title">Inloggen is niet ingericht</h2>
        <p className="atl-signin__body">
          Deze omgeving mist de instellingen van de identity provider. Inloggen kan pas als die er zijn; dat is
          werk voor de beheerder.
        </p>
        {problem && <p className="atl-signin__reference">{problem}</p>}
      </div>
    );
  }

  // status === "signed-out"
  if (mode === "entra") {
    return (
      <div className="atl-signin__state">
        <Icon tone="navy">
          <Users size={22} strokeWidth={1.75} aria-hidden />
        </Icon>
        <h2 className="atl-signin__title atl-signin__title--large">Inloggen</h2>
        <p className="atl-signin__body">
          U logt in met het Microsoft-account waarop u bent uitgenodigd. Het platform bewaart zelf geen
          wachtwoorden.
        </p>
        <Button
          variant="navy"
          className="atl-signin__microsoft"
          icon={<MicrosoftMark />}
          onClick={() => void onSignIn()}
        >
          Inloggen met Microsoft
        </Button>
        {problem && <p className="atl-signin__reference">{problem}</p>}
      </div>
    );
  }

  if (mode === "local") {
    return (
      <div className="atl-signin__state atl-signin__state--attention">
        <Icon tone="attention">
          <ShieldAlert size={20} strokeWidth={1.75} aria-hidden />
        </Icon>
        <h2 className="atl-signin__title">Lokale ontwikkelmodus</h2>
        <p className="atl-signin__body">
          In deze modus wordt niet ingelogd: de identiteit komt uit de omgeving. Er is er nu geen ingesteld.
        </p>
        <p className="atl-signin__body">
          Zet <code>VITE_DEV_TENANT_ID</code> (met <code>VITE_DEV_USER</code> en <code>VITE_DEV_ROLES</code>) of
          <code>VITE_DEV_ACCESS_TOKEN</code> in <code>frontend/.env.local</code> en herstart de dev-server.
        </p>
      </div>
    );
  }

  return (
    <div className="atl-signin__state atl-signin__state--attention">
      <Icon tone="attention">
        <LockKeyhole size={20} strokeWidth={1.75} aria-hidden />
      </Icon>
      <h2 className="atl-signin__title">Inloggen is nog niet ingeschakeld</h2>
      <p className="atl-signin__body">
        Deze build draait zonder identity provider en heeft daarom nergens toegang toe; dat is de
        standaardinstelling, niet een fout.
      </p>
      <p className="atl-signin__body">
        Zet <code>VITE_AUTH_MODE=entra</code> met de waarden die <code>infra/azure/08-entra-app.sh</code> afdrukt,
        of <code>VITE_AUTH_MODE=local</code> voor lokale ontwikkeling.
      </p>
    </div>
  );
}

/**
 * Het Microsoft-merkje in de vier merkkleuren, zoals de merkregels van
 * Microsoft voor "Inloggen met Microsoft" vragen. De kleuren staan in het
 * SVG-bestand (een merkasset), niet als hexcodes in de component.
 */
function MicrosoftMark() {
  return <img src={microsoftMark} width={16} height={16} alt="" aria-hidden className="atl-signin__ms" />;
}

function Icon({ tone, children }: { tone: "navy" | "attention" | "danger"; children: React.ReactNode }) {
  return (
    <span className={`atl-signin__icon atl-signin__icon--${tone}`} aria-hidden>
      {children}
    </span>
  );
}
